import { FormProvider, useForm } from "react-hook-form";
import { ThemeProvider } from "@emotion/react";
import { Box, createTheme } from "@mui/material";
import Fields from "./fields";
import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import { defaultFormTheme } from "../../../../../atoms/defaultFormTheme";
import { useSnackbar } from "notistack";
import { declineAgenda, PeAgendaStatus, revokeAgenda, submitAgendaResponse } from "../../../../accept-invitation-form/helper";
import { genericPostPatch } from "../../../../../utils/services";
import { useBoolean } from "usehooks-ts";
import DeclineSubmitBtns from "../DeclineSubmitBtns";
import { isExpert } from "../../../../../utils/role";
import { IConfiguredAgendaResponse, TCategorizedAgenda, TConfiguredAgendaItem } from "../../../../accept-invitation-form/type";
import CustomBtnFilled from "../../../../../atoms/form-molecules/CustomBtnFilled";
import ScrollIndicatorWrapper from "../../../../../atoms/scroll-indicator-wrapper";

type Props = {
  pe_agenda: TConfiguredAgendaItem;
  setPeAgendaData: Dispatch<SetStateAction<TCategorizedAgenda>>;
  pe_id: number;
  handleSubmitClose: () => void;
  isAcceptInvitation: boolean;
  expertId: number;
  pe_agenda_id: number
  project_id: number
  agenda_id: number
  handleNoSentAgendaExist: () => void
  showRevokeBtn?: boolean;
};

const SaveAgendaResponseForm = ({
  pe_agenda,
  setPeAgendaData,
  pe_id,
  pe_agenda_id,
  agenda_id,
  expertId,
  project_id,
  handleNoSentAgendaExist,
  isAcceptInvitation,
  showRevokeBtn = false,
}: Props) => {
  let defaultValues: any = {};
  const { enqueueSnackbar } = useSnackbar();
  const { value: submitLoading, setTrue: setSubmitLoadingTrue, setValue: setSubmitLoading } = useBoolean(false);
  const { value: declineLoading, setTrue: setDeclineLoadingTrue, setValue: setDeclineLoading } = useBoolean(false);
  const { value: revokeLoading, setTrue: setRevokeLoadingTrue, setValue: setRevokeLoading } = useBoolean(false);

  const methods = useForm({ defaultValues });

  const onSubmit = async (formData: any) => {
    try {
      setSubmitLoadingTrue();
      const agendaQuesResponse: IConfiguredAgendaResponse[] = [];
      let hasError = false;
      pe_agenda?.formattedAgenda?.forEach((item: any, index: number) => {
        const inputId = item.inputId;
        const answerKey = `${inputId}-answer-${index}`;
        const noteKey = `${inputId}-expert_note-${index}`;

        const answer = formData[answerKey];
        const expert_note = formData[noteKey]?.trim() || "";

        // Check if answer is empty
        if (!answer || answer.trim() === "") {
          hasError = true;
          methods.setError(answerKey, {
            type: "manual",
            message: "Answer is required",
          });
        }
        if (!expert_note && item?.expert_note_required) {
          hasError = true;
          methods.setError(noteKey, {
            type: "manual",
            message: "expert note is required",
          });
        }

        agendaQuesResponse.push({
          question: item.question,
          answer: answer,
          expert_note: expert_note,
          inputId: item.inputId,
          isNewQuestion: item.isNewQuestion,
        });
      });

      if (hasError) {
        enqueueSnackbar("Please fill in all required answers", { variant: "error" });
        return; // Stop submission
      }
      // Proceed only if all answers are valid
      if (pe_id) {
        const apiAgendaResponsePayload = agendaQuesResponse.map(({ question, answer, expert_note }) => ({
          question,
          answer,
          expert_note,
        }));
        await genericPostPatch(
          () => submitAgendaResponse({
            pe_agenda_id,
            agendaResponse: apiAgendaResponsePayload,
            pe_id,
            project_id,
            agenda_id,
          }),
          enqueueSnackbar,
          setSubmitLoading,
          () => {
            setPeAgendaData(({ sentAgenda, declineAgenda, respondedByAdminAgenda, respondedByExpertAgenda }) => {
              const updatedSentAgenda = sentAgenda.filter(agenda => agenda.id !== pe_agenda.id);
              const submittedAgenda = sentAgenda.find(agenda => agenda?.id === pe_agenda.id);
              const isRoleExpert = isExpert();
              let newRespondedByExpertAgenda = respondedByExpertAgenda;
              if (isRoleExpert && submittedAgenda) {
                const updatedAgenda: TConfiguredAgendaItem = {
                  ...submittedAgenda,
                  status: PeAgendaStatus.RESPONDED_BY_EXPERT,
                  formattedAgenda: agendaQuesResponse,
                };
                newRespondedByExpertAgenda = [...respondedByExpertAgenda, updatedAgenda];
              }
              return {
                sentAgenda: updatedSentAgenda,
                declineAgenda,
                respondedByAdminAgenda: isRoleExpert
                  ? respondedByAdminAgenda
                  : submittedAgenda
                    ? [...respondedByAdminAgenda, { ...submittedAgenda, status: PeAgendaStatus.RESPONDED_BY_ADMIN, formattedAgenda: agendaQuesResponse }]
                    : respondedByAdminAgenda,
                respondedByExpertAgenda: newRespondedByExpertAgenda,
              };
            });
            handleNoSentAgendaExist()
          }
        );
      }
    } catch (error) {
      console.error("Submit error:", error);
      enqueueSnackbar("Failed to submit agenda response", { variant: "error" });
    }
  };

  const handleDeclineBtn = async () => {
    if (!pe_agenda_id) return;

    try {
      setDeclineLoadingTrue();
      await genericPostPatch(
        () => declineAgenda({
          expertId: expertId,
          peAgendaId: pe_agenda_id,
          projectId: project_id,
          peId: pe_id
        }),
        enqueueSnackbar,
        setDeclineLoading,
        () => {
          setPeAgendaData(({ sentAgenda, declineAgenda, respondedByAdminAgenda, respondedByExpertAgenda }) => {
            const updatedSentAgenda = sentAgenda.filter(agenda => agenda.id !== pe_agenda.id);
            const declinedItem = sentAgenda.find(agenda => agenda?.id === pe_agenda.id);
            return {
              sentAgenda: updatedSentAgenda,
              declineAgenda: declinedItem ? [...declineAgenda, { ...declinedItem, status: PeAgendaStatus.DECLINED }] : declineAgenda,
              respondedByAdminAgenda,
              respondedByExpertAgenda
            };
          });
          handleNoSentAgendaExist()
        }
      );
    } catch (error) {
      console.error('Decline error:', error);
      enqueueSnackbar('Failed to decline agenda', { variant: 'error' });
    }
  }

  const handleRevokeBtnClick = async () => {
  if (!pe_agenda_id) return;

  try {
    setRevokeLoadingTrue(); // optional: reuse same loader
    await genericPostPatch(
      () => revokeAgenda({
        expertId: expertId,
        peAgendaId: pe_agenda_id,
        projectId: project_id,
        peId: pe_id
      }),
      enqueueSnackbar,
      setRevokeLoading,
      () => {
        setPeAgendaData(({ sentAgenda, declineAgenda, respondedByAdminAgenda, respondedByExpertAgenda }) => {
          const updatedSentAgenda = sentAgenda.filter(agenda => agenda.id !== pe_agenda.id);
          return {
            sentAgenda: updatedSentAgenda,
            declineAgenda,
            respondedByAdminAgenda,
            respondedByExpertAgenda
          };
        });
        handleNoSentAgendaExist()
      }
    );
  } catch (error) {
    console.error("Revoke error:", error);
    enqueueSnackbar("Failed to revoke agenda", { variant: "error" });
  }
};
  const formContainerRef = useRef<HTMLDivElement>(null);

  const defaultTheme = createTheme(defaultFormTheme);

  useEffect(() => {
    methods.reset();
    formContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [pe_agenda])

  return (
    <FormProvider {...methods}>
      <ThemeProvider theme={defaultTheme}>
        <ScrollIndicatorWrapper
        ref={formContainerRef}
          sx={{ maxHeight: "100%" }}
          contentKey={pe_agenda_id}
        >
          <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
            {pe_agenda?.formattedAgenda?.map((agenda, index: number) => (
              <Fields
                key={agenda.inputId}
                agendaObj={agenda}
                showQuestionStatus
                QuestionStatus={agenda.isNewQuestion ? "New" : "Reshared"}
                inputId={agenda.inputId}
                index={index}
                isLast={index === pe_agenda.formattedAgenda.length - 1}
              />
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {showRevokeBtn && <CustomBtnFilled
                label={revokeLoading ? "Revoking..." : "Revoke"}
                variant="outlined"
              disabled={revokeLoading || !isAcceptInvitation || submitLoading || declineLoading}
                onClick={handleRevokeBtnClick}
              />}

              <DeclineSubmitBtns
                cancelLabel={declineLoading ? "Declining..." : "Decline"}
                submitLabel={submitLoading ? "Submitting..." : "Submit & Next"}
                isSubmitDisabled={submitLoading || !isAcceptInvitation || declineLoading || revokeLoading}
                isCancelDisabled={declineLoading || !isAcceptInvitation || declineLoading || revokeLoading}
                handleClose={handleDeclineBtn}
              />
            </Box>
          </form>
        </ScrollIndicatorWrapper>
      </ThemeProvider>
    </FormProvider>
  );
};

export default SaveAgendaResponseForm;
